<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { onBeforeMount, onUnmounted } from "vue";
import dayjs from "dayjs";
import {
  PTooltip,
  IconWhatsAppBusinessApiXL as WabaIcon,
  IconCrossSm,
  PHint,
  PButton,
} from "@profeat/ui-kit";

import { useEvents, Event } from "~/composables/events";
import { useBroadcast } from "~/stores/broadcast.store";
import { Channel } from "~/entities/channel";

const { onUpdateMessage, stopSendingBroadcast } = useBroadcast();

const events = useEvents();

const { statistics } = storeToRefs(useBroadcast());

const calcPrc = (base: number, val: number) => {
  if (base == 0 || val == 0) {
    return 0;
  }
  return ((val * 100) / base).toFixed();
};
const getDate = (createdAt: Date) => dayjs(createdAt).format("DD.MM.YYYY");

const isForming = (date: Date, isFinished: boolean) =>
  isFinished && dayjs(new Date()).diff(date, "day") < 1;

const breakSender = async (id: string) => {
  await stopSendingBroadcast(id);
};

/**
 * При формировании данных для строк в поле "доставлено"
 * помещаем максимальное значение из двух значений - доставлено и прочитано
 * т.к. пока не все события о доставке приходят со стороны ваззап
 * Это касается всех типов рассылки
 */
const deliveredCount = (delivered: number, read: number) =>
  Math.max(delivered, read);

onBeforeMount(() => {
  events.subscribe(Event.MessageStatus, onUpdateMessage);
});

onUnmounted(() => {
  events.unSubscribe(Event.MessageStatus, onUpdateMessage);
});

function channelTitle(channel: Channel | null) {
  if (!channel) return "";
  return channel.phone || channel.username;
}
</script>

<template>
  <div class="table">
    <div class="header">
      {{ $t("statistics.date") }}
    </div>
    <div class="header">
      {{ $t("statistics.broadcast") }}
    </div>
    <div class="header">Канал</div>
    <div class="header">
      {{ $t("statistics.sent") }}
      <PTooltip placement="bottom">
        <template #activator="{ setReference, click }">
          <IconInfoSm
            :ref="setReference"
            @click="click"
            class="color-[#91A0AE]"
          />
        </template>

        <template #text>
          <span style="color: var(--common-black)"
            >Фактически отправлено / Количество контактов в рассылке</span
          >
        </template>
      </PTooltip>
    </div>
    <div class="header">
      {{ $t("statistics.delivered") }}
    </div>
    <div class="header">
      {{ $t("statistics.read") }}
    </div>
    <div class="header">
      <span>{{ $t("statistics.answered") }}</span>
      <PTooltip placement="bottom">
        <template #activator="{ setReference, click }">
          <IconInfoSm
            :ref="setReference"
            @click="click"
            class="color-[#91A0AE]"
          />
        </template>

        <template #text>
          <span style="color: var(--common-black)">{{
            $t("statistics.answeredTooltip")
          }}</span>
        </template>
      </PTooltip>
    </div>
    <div class="header"></div>

    <template v-for="stat in statistics">
      <div
        :style="{
          borderLeft: '1px solid #E5EBF0',
          borderRadius: '16px 0 0 16px',
        }"
        class="cell"
      >
        {{ getDate(stat.createdAt) }}
      </div>
      <div class="cell">
        <div class="truncate">{{ stat.broadcastTitle }}</div>
      </div>
      <div class="cell">
        <div class="flex">
          <WabaIcon class="w-[20px] h-[20px]" />
        </div>
        {{ channelTitle(stat.channel) }}
      </div>
      <div class="cell">
        {{ stat.sentCount }}&nbsp;/&nbsp;{{ stat.totalCount }}
      </div>
      <div class="cell">
        <div>{{ deliveredCount(stat.deliveredCount, stat.readCount) }}</div>
        <div class="secondary">
          ({{
            calcPrc(
              stat.sentCount,
              deliveredCount(stat.deliveredCount, stat.readCount)
            )
          }}%)
        </div>
      </div>
      <div class="cell">
        <div>{{ stat.readCount }}</div>
        <div class="secondary">
          ({{ calcPrc(stat.sentCount, stat.readCount) }}%)
        </div>
      </div>
      <div class="cell">
        <div>{{ stat.answeredCount }}</div>
        <div class="secondary">
          ({{ calcPrc(stat.sentCount, stat.answeredCount) }}%)
        </div>
      </div>
      <div
        class="cell"
        :style="{
          borderRight: '1px solid #E5EBF0',
          borderRadius: '0 16px 16px 0',
        }"
      >
        <div
          v-if="
            stat.lastDeliveryAt &&
            isForming(stat.lastDeliveryAt, stat.isDelivered)
          "
        >
          <BaseBadge status="info" :text="$t('statistics.formingBadge')" />
        </div>

        <div
          class="flex items-center gap-[4px] flex-center"
          v-if="!stat.isDelivered"
        >
          <BaseBadge status="warning" :text="$t('statistics.inQueue')" />
          <PHint placement="top">
            <template #activator="{ setReference, onHover, outHover }">
              <PButton
                class="p-4px"
                :ref="setReference"
                @mouseover="onHover"
                @mouseleave="outHover"
                variant="text"
                color="asphalt"
                @click="breakSender(stat.broadcastId)"
              >
                <IconCrossSm />
              </PButton>
            </template>
            <template #hint>
              {{ $t("broadcast.stopSendingBroadcast") }}
            </template>
          </PHint>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.secondary {
  color: $typography-secondary-color;
  font-size: 12px;
}

.header {
  padding: 8px 16px;
  text-align: left;
  color: var(--common-asphalt);
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  grid-gap: 5px;
  cursor: pointer;
  height: 32px;
  margin-bottom: -8px;

  :deep {
    .p-tooltip-desktop .target {
      display: flex !important;
    }
  }
}

.cell {
  display: flex;
  padding: 22px 16px;
  align-items: center;
  border-top: 1px solid var(--common-smog);
  border-bottom: 1px solid var(--common-smog);
  gap: 5px;
  height: 64px;
}

.column {
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
}

.table {
  display: grid;
  grid-template-columns: auto auto auto auto auto auto auto auto;
  width: 100%;
  row-gap: 8px;
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 256px;
}
</style>
